import { language } from '../services/dictionary.service';

/**
 * Maps API message codes to localized messages in English, Arabic, and French.
 * Codes starting with 'S' are success codes; 'E' are error codes.
 */
export const MessageCodeMap: Record<string, Record<language, string>> = {

    // ─── Customer Success ────────────────────────────────────────────────────
    S0701001: {
        en: 'Customer created successfully',
        ar: 'تم إنشاء العميل بنجاح',
        fr: 'Client créé avec succès',
    },
    S0706001: {
        en: 'Customer updated successfully',
        ar: 'تم تحديث العميل بنجاح',
        fr: 'Client mis à jour avec succès',
    },
    S0702001: {
        en: 'Customer emails updated successfully',
        ar: 'تم تحديث بريد العملاء بنجاح',
        fr: 'Emails des clients mis à jour avec succès',
    },
    S0703001: {
        en: 'Customer geo-locations updated successfully',
        ar: 'تم تحديث مواقع العملاء الجغرافية بنجاح',
        fr: 'Géolocalisations des clients mises à jour avec succès',
    },
    S0704001: {
        en: 'Customers updated successfully',
        ar: 'تم تحديث العملاء بنجاح',
        fr: 'Clients mis à jour avec succès',
    },
    S0705001: {
        en: 'Customers retrieved successfully',
        ar: 'تم استرداد العملاء بنجاح',
        fr: 'Clients récupérés avec succès',
    },
    S0707001: {
        en: 'Company customers retrieved successfully',
        ar: 'تم استرداد عملاء الشركة بنجاح',
        fr: 'Clients de la société récupérés avec succès',
    },
    S0708001: {
        en: 'User customers retrieved successfully',
        ar: 'تم استرداد عملاء المستخدم بنجاح',
        fr: "Clients de l'utilisateur récupérés avec succès",
    },
    S0709001: {
        en: 'Customer classes retrieved successfully',
        ar: 'تم استرداد تصنيفات العملاء بنجاح',
        fr: 'Classes des clients récupérées avec succès',
    },

    // ─── Customer Draft Success ───────────────────────────────────────────────
    S0801001: {
        en: 'Customer creation draft submitted successfully',
        ar: 'تم إرسال مسودة إنشاء العميل بنجاح',
        fr: 'Brouillon de création du client soumis avec succès',
    },
    S0802001: {
        en: 'Customer edit submitted for approval',
        ar: 'تم إرسال تعديل العميل للموافقة',
        fr: 'Modification du client soumise pour approbation',
    },
    S0803001: {
        en: 'Customers added to deletion draft',
        ar: 'تمت إضافة العملاء إلى مسودة الحذف',
        fr: 'Clients ajoutés au brouillon de suppression',
    },
    S0804001: {
        en: 'Creation drafts approved successfully',
        ar: 'تمت الموافقة على مسودات الإنشاء بنجاح',
        fr: 'Brouillons de création approuvés avec succès',
    },
    S0805001: {
        en: 'Editing drafts approved successfully',
        ar: 'تمت الموافقة على مسودات التعديل بنجاح',
        fr: 'Brouillons de modification approuvés avec succès',
    },
    S0806001: {
        en: 'Deletion drafts approved successfully',
        ar: 'تمت الموافقة على مسودات الحذف بنجاح',
        fr: 'Brouillons de suppression approuvés avec succès',
    },
    S0807001: {
        en: 'Drafts rejected successfully',
        ar: 'تم رفض المسودات بنجاح',
        fr: 'Brouillons rejetés avec succès',
    },
    S0808001: {
        en: 'Drafts retrieved successfully',
        ar: 'تم استرداد المسودات بنجاح',
        fr: 'Brouillons récupérés avec succès',
    },
    S0809001: {
        en: 'Supervisor drafts retrieved successfully',
        ar: 'تم استرداد مسودات المشرف بنجاح',
        fr: 'Brouillons du superviseur récupérés avec succès',
    },
    S0810001: {
        en: 'Draft table retrieved successfully',
        ar: 'تم استرداد جدول المسودات بنجاح',
        fr: 'Tableau de brouillons récupéré avec succès',
    },
    S0811001: {
        en: 'Supervisor draft table retrieved successfully',
        ar: 'تم استرداد جدول مسودات المشرف بنجاح',
        fr: 'Tableau de brouillons du superviseur récupéré avec succès',
    },
    S0812001: {
        en: 'Draft removed successfully',
        ar: 'تمت إزالة المسودة بنجاح',
        fr: 'Brouillon supprimé avec succès',
    },

    // ─── Customer Errors ────────────────────────────────────────────────────
    E0706001: {
        en: 'Customer is not assigned to this representative',
        ar: 'العميل غير مخصص لهذا المندوب',
        fr: "Le client n'est pas assigné à ce représentant",
    },
    E0706002: {
        en: 'Customer not found',
        ar: 'لم يتم العثور على العميل',
        fr: 'Client introuvable',
    },
    E0702001: {
        en: 'Some customers are not assigned to this representative',
        ar: 'بعض العملاء غير مخصصين لهذا المندوب',
        fr: "Certains clients ne sont pas assignés à ce représentant",
    },
    E0703001: {
        en: 'Some customers geo-locations could not be updated — not assigned to this representative',
        ar: 'لا يمكن تحديث مواقع بعض العملاء — غير مخصصين لهذا المندوب',
        fr: "Impossible de mettre à jour la géolocalisation de certains clients — non assignés à ce représentant",
    },
    E0704001: {
        en: 'Some customers are not assigned to this representative',
        ar: 'بعض العملاء غير مخصصين لهذا المندوب',
        fr: "Certains clients ne sont pas assignés à ce représentant",
    },
    E0704002: {
        en: 'Some customers were not found for bulk update',
        ar: 'لم يتم العثور على بعض العملاء للتحديث الجماعي',
        fr: 'Certains clients introuvables pour la mise à jour en masse',
    },
    E0704003: {
        en: 'Some user customers were not found for bulk update',
        ar: 'لم يتم العثور على بعض عملاء المستخدم للتحديث الجماعي',
        fr: "Certains clients utilisateur introuvables pour la mise à jour en masse",
    },
    E0705001: {
        en: 'Customers not found',
        ar: 'لم يتم العثور على العملاء',
        fr: 'Clients introuvables',
    },

    // ─── Customer Draft Errors ───────────────────────────────────────────────
    E0801001: {
        en: 'Some customers were not found',
        ar: 'لم يتم العثور على بعض العملاء',
        fr: 'Certains clients introuvables',
    },
    E0801002: {
        en: 'Invalid deadline for draft',
        ar: 'الموعد النهائي للمسودة غير صالح',
        fr: 'Délai de brouillon invalide',
    },
    E0804001: {
        en: 'Customer draft not found for approval',
        ar: 'لم يتم العثور على مسودة العميل للموافقة',
        fr: 'Brouillon client introuvable pour approbation',
    },
    E0804002: {
        en: 'Some creation drafts were not found',
        ar: 'لم يتم العثور على بعض مسودات الإنشاء',
        fr: 'Certains brouillons de création introuvables',
    },
    E0804003: {
        en: 'Invalid deadline for creation drafts',
        ar: 'الموعد النهائي لمسودات الإنشاء غير صالح',
        fr: 'Délai invalide pour les brouillons de création',
    },
    E0805001: {
        en: 'Customer draft not found for editing approval',
        ar: 'لم يتم العثور على مسودة العميل للموافقة على التعديل',
        fr: 'Brouillon client introuvable pour approbation de modification',
    },
    E0805002: {
        en: 'User customers draft not found',
        ar: 'لم يتم العثور على مسودة عملاء المستخدم',
        fr: "Brouillon de clients utilisateur introuvable",
    },
    E0805003: {
        en: 'Some editing drafts were not found',
        ar: 'لم يتم العثور على بعض مسودات التعديل',
        fr: 'Certains brouillons de modification introuvables',
    },
    E0805004: {
        en: 'Invalid deadline for editing drafts',
        ar: 'الموعد النهائي لمسودات التعديل غير صالح',
        fr: 'Délai invalide pour les brouillons de modification',
    },
    E0806001: {
        en: 'Customer draft not found for deletion approval',
        ar: 'لم يتم العثور على مسودة العميل للموافقة على الحذف',
        fr: 'Brouillon client introuvable pour approbation de suppression',
    },
    E0806002: {
        en: 'Some deletion drafts were not found',
        ar: 'لم يتم العثور على بعض مسودات الحذف',
        fr: 'Certains brouillons de suppression introuvables',
    },
    E0806003: {
        en: 'Invalid deadline for deletion drafts',
        ar: 'الموعد النهائي لمسودات الحذف غير صالح',
        fr: 'Délai invalide pour les brouillons de suppression',
    },
    E0807001: {
        en: 'Some drafts were not found for rejection',
        ar: 'لم يتم العثور على بعض المسودات للرفض',
        fr: 'Certains brouillons introuvables pour le rejet',
    },
    E0807002: {
        en: 'Invalid deadline for drafts rejection',
        ar: 'الموعد النهائي لرفض المسودات غير صالح',
        fr: 'Délai invalide pour le rejet des brouillons',
    },
    E0807003: {
        en: 'Customer draft not found for rejection',
        ar: 'لم يتم العثور على مسودة العميل للرفض',
        fr: 'Brouillon client introuvable pour le rejet',
    },
    E0812001: {
        en: 'Draft not found',
        ar: 'لم يتم العثور على المسودة',
        fr: 'Brouillon introuvable',
    },
    E0812002: {
        en: 'Draft item not found',
        ar: 'لم يتم العثور على عنصر المسودة',
        fr: 'Élément de brouillon introuvable',
    },
    E0812003: {
        en: 'Failed to delete draft',
        ar: 'فشل حذف المسودة',
        fr: 'Échec de la suppression du brouillon',
    },

    // ─── Validation Errors ───────────────────────────────────────────────────
    E2801001: {
        en: 'Bad request — please check the submitted data',
        ar: 'طلب غير صالح — يرجى التحقق من البيانات المُرسلة',
        fr: 'Requête invalide — veuillez vérifier les données soumises',
    },
};

/**
 * Retrieves the localized message for a given message code.
 * @param code - The API message code (e.g. 'S0701001')
 * @param lang - The current language
 * @returns The localized string, or null if the code is not found
 */
export function getMessageByCode(code: string, lang: language): string | null {
    const entry = MessageCodeMap[code];
    if (!entry) return null;
    return entry[lang] ?? entry['en'];
}

/**
 * Determines the snackbar type based on the message code prefix.
 * 'S' → 'success', 'E' → 'error', anything else → 'default'
 */
export function getTypeByCode(code: string): 'success' | 'error' | 'default' {
    if (code.startsWith('S')) return 'success';
    if (code.startsWith('E')) return 'error';
    return 'default';
}
